import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Lock } from 'lucide-react';
import IdleReasonModal from './IdleReasonModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function PinBoxes({ value, onChange }) {
  const refs = useRef([]);

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = value.slice(0, idx) + value.slice(idx + 1);
      onChange(next);
      if (idx > 0) refs.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      refs.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowRight' && idx < 5) {
      refs.current[idx + 1]?.focus();
    }
  };

  const handleInput = (idx, e) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1);
    if (!char) return;
    const arr = (value.padEnd(6, ' ')).split('');
    arr[idx] = char;
    const next = arr.join('').replace(/ /g, '');
    onChange(next);
    if (idx < 5) refs.current[idx + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (text) {
      onChange(text);
      refs.current[Math.min(text.length, 5)]?.focus();
    }
  };

  return (
    <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
      {Array.from({ length: 6 }).map((_, idx) => (
        <input
          key={idx}
          ref={el => refs.current[idx] = el}
          type="password"
          inputMode="numeric"
          maxLength={1}
          value={value[idx] || ''}
          onChange={e => handleInput(idx, e)}
          onKeyDown={e => handleKeyDown(idx, e)}
          onPaste={handlePaste}
          autoFocus={idx === 0}
          style={{
            width: 46, height: 58, textAlign: 'center', fontSize: 22,
            fontWeight: 700, fontFamily: 'monospace',
            border: '2px solid rgba(255,255,255,0.25)',
            borderRadius: 12, background: 'rgba(255,255,255,0.1)',
            color: '#fff', outline: 'none', transition: 'border-color 0.15s',
          }}
          onFocus={e => { e.target.style.borderColor = 'rgba(89,165,44,0.8)'; }}
          onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.25)'; }}
        />
      ))}
    </div>
  );
}

export default function SessionLockScreen() {
  const { user, sessionLocked, unlockSession, logout } = useAuth();
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasPinLive, setHasPinLive] = useState(null);
  const [idleReasonSubmitted, setIdleReasonSubmitted] = useState(
    () => sessionStorage.getItem('idle_reason_submitted') === 'true'
  );

  // Fetch fresh has_pin status every time lock screen appears (avoids stale context value)
  useEffect(() => {
    if (!sessionLocked) { setHasPinLive(null); return; }
    const token = localStorage.getItem('auth_token');
    fetch(`${API_URL}/api/pins/my-status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => setHasPinLive(d !== null ? !!d.has_pin : (user?.has_pin ?? false)))
      .catch(() => setHasPinLive(user?.has_pin ?? false));
  }, [sessionLocked, user?.has_pin]);

  if (!sessionLocked) return null;

  const hasPin = hasPinLive ?? user?.has_pin;
  const displayName = user?.full_name || user?.name || user?.email || 'User';
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pin.length !== 6) { setError('Enter your 6-digit PIN'); return; }
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/auth/verify-credit-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pin }),
      });
      if (res.ok) {
        setPin('');
        unlockSession();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Incorrect PIN. Try again.');
        setPin('');
      }
    } catch {
      setError('Connection error. Try again.');
    }
    setLoading(false);
  };

  // Show idle reason modal first if reason not yet submitted for this idle period
  if (!idleReasonSubmitted) {
    return (
      <IdleReasonModal
        onSubmit={() => { sessionStorage.setItem('idle_reason_submitted', 'true'); setIdleReasonSubmitted(true); }}
        onDismiss={() => { sessionStorage.setItem('idle_reason_submitted', 'true'); setIdleReasonSubmitted(true); }}
      />
    );
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: 'rgba(1, 38, 151, 0.93)',
      backdropFilter: 'blur(10px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: 'rgba(255,255,255,0.07)',
        border: '1px solid rgba(255,255,255,0.14)',
        borderRadius: 22, padding: '40px 36px',
        width: 360, maxWidth: '92vw', textAlign: 'center',
        boxShadow: '0 32px 80px rgba(0,0,0,0.45)',
      }}>
        {/* Avatar */}
        <div style={{
          width: 68, height: 68, borderRadius: '50%',
          background: 'rgba(255,255,255,0.14)',
          border: '2px solid rgba(255,255,255,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 18px',
          fontSize: 24, fontWeight: 700, color: '#fff', letterSpacing: '0.02em',
        }}>
          {initials}
        </div>

        {/* Lock badge */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, marginBottom: 6 }}>
          <Lock style={{ width: 13, height: 13, color: 'rgba(255,255,255,0.5)' }} />
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.09em', fontWeight: 600 }}>
            Session Locked
          </span>
        </div>

        <h2 style={{ color: '#fff', fontSize: 19, fontWeight: 700, marginBottom: 6, fontFamily: 'Cinzel, serif' }}>
          {displayName}
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, marginBottom: 28 }}>
          {hasPin
            ? 'Session locked due to inactivity. Please enter your PIN to continue.'
            : 'Your session has been locked due to inactivity.'}
        </p>

        {!hasPin ? (
          <>
            <div style={{
              background: 'rgba(255,190,0,0.12)', border: '1px solid rgba(255,190,0,0.25)',
              borderRadius: 10, padding: '12px 14px', marginBottom: 22,
              color: 'rgba(255,220,80,0.9)', fontSize: 13, lineHeight: 1.5,
            }}>
              You haven't set up a security PIN yet.<br />Please contact an administrator.
            </div>
            <button
              onClick={logout}
              style={{
                width: '100%', padding: '12px 0', borderRadius: 11, border: 'none',
                cursor: 'pointer', fontSize: 14, fontWeight: 600,
                background: 'rgba(255,255,255,0.1)', color: '#fff',
              }}
            >
              Sign Out
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 20 }}>
              <PinBoxes value={pin} onChange={v => { setPin(v); setError(''); }} />
            </div>

            {error && (
              <p style={{ color: '#ff7070', fontSize: 13, marginBottom: 16 }}>{error}</p>
            )}

            <button
              type="submit"
              disabled={loading || pin.length !== 6}
              style={{
                width: '100%', padding: '13px 0', borderRadius: 11, border: 'none',
                cursor: pin.length === 6 && !loading ? 'pointer' : 'not-allowed',
                fontSize: 15, fontWeight: 700,
                background: pin.length === 6 ? '#59a52c' : 'rgba(255,255,255,0.1)',
                color: '#fff', transition: 'background 0.2s',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Verifying…' : 'Unlock Session'}
            </button>

            <button
              type="button"
              onClick={logout}
              style={{
                marginTop: 16, width: '100%', background: 'none', border: 'none',
                cursor: 'pointer', color: 'rgba(255,255,255,0.35)',
                fontSize: 12, textDecoration: 'underline',
              }}
            >
              Sign out instead
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
